import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthCtx = createContext({
  user: null,
  loading: true,
  loginWithPassword: async () => {},
  loginWithPin: async () => {},
  logout: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("it_token");
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (e) {
        localStorage.removeItem("it_token");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loginWithPassword = async (email, password, rememberDevice) => {
    const { data } = await api.post("/auth/login", {
      email,
      password,
      remember_device: rememberDevice,
      device_name: navigator.userAgent.split(" ").slice(-2).join(" "),
    });
    if (data.otp_required) {
      return { otpRequired: true, otpToken: data.otp_token, channel: data.channel, sentTo: data.sent_to };
    }
    localStorage.setItem("it_token", data.token);
    if (data.device_token) {
      localStorage.setItem("it_device_token", data.device_token);
      localStorage.setItem("it_pin_email", email);
    }
    setUser(data.user);
    return data.user;
  };

  const verifyLoginOtp = async (otpToken, otp, rememberDevice, email) => {
    const { data } = await api.post("/auth/login-otp-verify", {
      otp_token: otpToken,
      otp,
      remember_device: !!rememberDevice,
      device_name: navigator.userAgent.split(" ").slice(-2).join(" "),
    });
    localStorage.setItem("it_token", data.token);
    if (data.device_token) {
      localStorage.setItem("it_device_token", data.device_token);
      if (email) localStorage.setItem("it_pin_email", email);
    }
    setUser(data.user);
    return data.user;
  };

  const resendLoginOtp = async (otpToken) => {
    const { data } = await api.post("/auth/login-otp-resend", { otp_token: otpToken });
    return data;
  };

  const loginWithPin = async (email, pin) => {
    const { data } = await api.post("/auth/pin-login", { email, pin });
    localStorage.setItem("it_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("it_token");
    localStorage.removeItem("it_admin_session");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthCtx.Provider value={{ user, loading, loginWithPassword, loginWithPin, verifyLoginOtp, resendLoginOtp, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  const perms = ctx.user?.permissions || [];
  const isOwner = ctx.user?.role === "owner" || ctx.user?.is_owner;
  const can = (...need) => isOwner || need.some((p) => perms.includes(p));
  return { ...ctx, perms, isOwner, can };
};
